/**
 * Authentication utility functions for testing
 * Exports the core authentication functions separately from the React hook
 */

/**
 * Silent refresh with exponential backoff retry
 * @param {number} retryCount - Current retry attempt
 * @param {Function} refreshSession - Function to call for actual refresh
 * @returns {Promise<boolean>} True if refresh succeeded
 */
export const silentRefreshWithBackoff = async (retryCount = 0, refreshSession) => {
    const maxRetries = 5;
    const baseDelay = 1000; // 1 second
    
    try {
        const result = await refreshSession();
        return result.success;
    } catch (error) {
        if (retryCount >= maxRetries) {
            console.error('Max refresh retries reached:', error);
            localStorage.setItem('isLoggedIn', 'false');
            return false;
        }
        
        const delay = Math.min(baseDelay * Math.pow(2, retryCount), 16000); // Max 16s
        console.log(`Refresh failed, retrying in ${delay}ms... (attempt ${retryCount + 1}/${maxRetries})`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return silentRefreshWithBackoff(retryCount + 1, refreshSession);
    }
};

/**
 * Monitor token expiration and trigger refresh
 * @param {Function} refreshSession - Function to call for actual refresh
 */
export const monitorTokenExpiration = async (refreshSession) => {
    // Import the function from jwtUtils
    const { isStoredTokenAboutToExpire } = await import('./jwtUtils.js');
    
    if (isStoredTokenAboutToExpire()) {
        console.log('Token about to expire, initiating silent refresh...');
        const success = await silentRefreshWithBackoff(0, refreshSession);
        if (!success) {
            console.warn('Silent refresh failed, user will be redirected on next API call');
        }
    }
};

/**
 * Mock refresh session for testing
 */
export const mockRefreshSession = async (shouldSucceed = true) => {
    if (shouldSucceed) {
        return { success: true };
    } else {
        throw new Error('Mock refresh failed');
    }
};